import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import Button from '@shared/ui/Button';

export interface RefreshButtonProps {
  /**
   * Called on click. If it returns a promise the icon spins and the button
   * disables until it settles.
   */
  onRefresh: () => void | Promise<unknown>;
  /**
   * External busy state, for callers that already track one (react-query
   * isFetching, a refreshing flag). Combined with the internal promise state.
   */
  refreshing?: boolean;
  /** Accessible name and, unless iconOnly, the visible label. */
  label?: string;
  /** Compact toolbar form: icon alone, the label via aria-label/title. */
  iconOnly?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * The Refresh control, in one place.
 *
 * Ten surfaces each hand-rolled this — as labeled secondary buttons, labeled
 * outline buttons, raw <button>s with their own spin logic, and bare icon
 * buttons (one with no accessible name at all). Labeled form is a secondary
 * Button; icon-only form is a ghost Button carrying the label as
 * aria-label/title. The RefreshCw icon itself spins while busy, which is the
 * recognizable idiom — so this deliberately does not use Button's `loading`
 * swap to a generic spinner.
 */
const RefreshButton: React.FC<RefreshButtonProps> = ({
  onRefresh,
  refreshing = false,
  label = 'Refresh',
  iconOnly = false,
  disabled = false,
  className = '',
}) => {
  const [pending, setPending] = useState(false);
  const busy = refreshing || pending;

  const handleClick = () => {
    const result = onRefresh();
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      setPending(true);
      (result as Promise<unknown>).finally(() => setPending(false));
    }
  };

  return (
    <Button
      variant={iconOnly ? 'ghost' : 'secondary'}
      size="sm"
      onClick={handleClick}
      disabled={disabled || busy}
      ariaLabel={iconOnly ? label : undefined}
      title={iconOnly ? label : undefined}
      className={className}
    >
      <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} aria-hidden="true" />
      {!iconOnly && label}
    </Button>
  );
};

export default RefreshButton;
